/**
 * ChecklistTemplatesPage - Listagem de Template de Checklist
 *
 * Menu: Configurações › Template de Checklist. Lista modelos com status, versão
 * vigente e aviso de alterações não publicadas. Criação abre o editor.
 *
 * @version 1.3.0
 * RELEASE: 2026-09-26 v1.3.0 - Adicionada coluna Tipo de Checklist na listagem.
 * RELEASE: 2026-09-26 v1.2.0 - Padronização visual com Departamentos: status, filtros e botões compactos.
 * RELEASE: 2026-09-26 v1.1.0 - Tipo de Checklist obrigatório na criação e botão "Tipos".
 * RELEASE: 2026-09-26 v1.0.0 - Criação inicial (Fase 1b)
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Copy, MoreHorizontal, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import {
  ListPageLayout, ListPageHeader, ListPageFiltersBar, ListPageContent,
  LIST_FILTERS_INPUT_CLASS, LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { usePermissions } from '@/hooks/usePermissions';
import { useChecklistModule } from '@/hooks/useChecklistModule';
import {
  useChecklistTemplatesList, useChecklistTemplateActions, useCreateChecklistTemplate,
  type ChecklistTemplateListRow,
} from '@/hooks/useChecklistTemplates';
import { InlineNotice } from '@/components/ui/inline-notice';
import { ChecklistTypesDialog, ChecklistTypeSelect } from '@/components/checklist/ChecklistTypesDialog';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';

export default function ChecklistTemplatesPage() {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const { isEnabled, isLoading: moduleLoading } = useChecklistModule();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const { data = [], isLoading, isFetching } = useChecklistTemplatesList({ search, status });
  const { duplicate, remove, setStatus: changeStatus } = useChecklistTemplateActions();
  const create = useCreateChecklistTemplate();

  // Diálogos
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('');
  const [typesOpen, setTypesOpen] = useState(false);
  const [toDelete, setToDelete] = useState<ChecklistTemplateListRow | null>(null);

  const allowCreate = canCreate('checklist_templates');
  const allowEdit = canEdit('checklist_templates');
  const allowDelete = canDelete('checklist_templates');

  /** Cria o modelo e abre o editor */
  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || !newType) return;
    const id = await create.mutateAsync({ name, type_id: newType });
    setNewOpen(false);
    setNewName('');
    setNewType('');
    navigate(`/tenant/settings/checklist-templates/${id}`);
  };

  if (!moduleLoading && !isEnabled) {
    return (
      <ListPageLayout>
        <ListPageHeader title="Template de Checklist" />
        <ListPageContent>
          <div className="mx-auto max-w-2xl">
            <InlineNotice variant="warning">
              O módulo Checklist está desligado. Um administrador pode ligá-lo em Configurações › aba Checklist.
            </InlineNotice>
          </div>
        </ListPageContent>
      </ListPageLayout>
    );
  }

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Template de Checklist"
        description="Modelos reutilizáveis nas vistorias e em outros módulos."
        actions={(
          <div className="flex gap-2">
            <Button size="xs" variant="outline" onClick={() => setTypesOpen(true)}>Tipos</Button>
            {allowCreate && (
              <Button size="xs" onClick={() => setNewOpen(true)}>
                <Plus className="mr-1.5 h-4 w-4" /> Novo modelo
              </Button>
            )}
          </div>
        )}
      />
      <ListPageFiltersBar isFetching={isFetching} className="flex-nowrap">
        <Input
          className={`${LIST_FILTERS_INPUT_CLASS} min-w-0 flex-1 sm:max-w-sm`}
          placeholder="Buscar modelo..."
          value={search}
          maxLength={100}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      <ListPageContent>
        {isLoading ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-muted-foreground">
            <ClipboardList className="h-8 w-8" />
            <p className="text-sm">Nenhum modelo encontrado.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Modelo</TableHead>
                <TableHead className="w-44">Tipo de Checklist</TableHead>
                <TableHead className="w-28">Versão</TableHead>
                <TableHead className="w-24 text-center">Seções</TableHead>
                <TableHead className="w-24 text-center">Itens</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((t) => (
                <TableRow
                  key={t.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/tenant/settings/checklist-templates/${t.id}`)}
                >
                  <TableCell>
                    <div className="font-medium">{t.name}</div>
                    {t.description && <div className="line-clamp-1 text-xs text-muted-foreground">{t.description}</div>}
                  </TableCell>
                  <TableCell>
                    <span className="line-clamp-1 text-sm">{t.type_name || '—'}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm">{t.current_version_number ? `v${t.current_version_number}` : 'Não publicada'}</span>
                      {t.has_unpublished_changes && (
                        <Badge variant="outline" className="w-fit text-[10px]">Alterações pendentes</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">{t.sections_count}</TableCell>
                  <TableCell className="text-center">{t.items_count}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                    >
                      <StatusDot tone={t.status === 'active' ? 'emerald' : 'muted'} />
                      {t.status === 'active' ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/tenant/settings/checklist-templates/${t.id}`)}>
                          <Pencil className="mr-2 h-4 w-4" /> Abrir
                        </DropdownMenuItem>
                        {allowCreate && (
                          <DropdownMenuItem onClick={() => duplicate.mutate({ id: t.id, name: `${t.name} (cópia)`.slice(0, 150) })}>
                            <Copy className="mr-2 h-4 w-4" /> Duplicar
                          </DropdownMenuItem>
                        )}
                        {allowEdit && (
                          <DropdownMenuItem onClick={() => changeStatus.mutate({ id: t.id, status: t.status === 'active' ? 'inactive' : 'active' })}>
                            <Power className="mr-2 h-4 w-4" /> {t.status === 'active' ? 'Inativar' : 'Ativar'}
                          </DropdownMenuItem>
                        )}
                        {allowDelete && !t.ever_published && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => setToDelete(t)}>
                              <Trash2 className="mr-2 h-4 w-4" /> Excluir
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListPageContent>

      {/* Novo modelo */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Novo modelo de checklist</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-tpl-name">Nome</Label>
            <Input
              id="new-tpl-name"
              autoFocus
              maxLength={150}
              value={newName}
              placeholder="Ex.: Vistoria de entrega"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <Label>Tipo de Checklist</Label>
            <ChecklistTypeSelect value={newType} onChange={setNewType} />
          </div>
          <DialogFooter>
            <Button size="xs" variant="outline" onClick={() => setNewOpen(false)}>Cancelar</Button>
            <Button size="xs" onClick={handleCreate} disabled={!newName.trim() || !newType || create.isPending}>Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChecklistTypesDialog open={typesOpen} onOpenChange={setTypesOpen} canEdit={allowEdit || allowCreate} />

      {/* Excluir (somente nunca publicado) */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir modelo?</AlertDialogTitle>
            <AlertDialogDescription>
              "{toDelete?.name}" nunca foi publicado e será excluído com suas seções e itens.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => toDelete && remove.mutate(toDelete.id)}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ListPageLayout>
  );
}
